import { Trial, FVProfile, VelocityLossMetrics, LoadVelocityMetrics, PowerDistribution, MechanicalEfficiency, TrainingReadiness, PerformanceRatios, DistanceMetrics, ValidationMetrics, MechanicalAnalysis, FatigueMetrics, AdvancedProfiling, InjuryPreventionIndicators } from '../types';

const RACE_PACE_ESTIMATES: Record<string, number> = {
  "Football": 8.5,
  "Soccer": 8.0,
  "Rugby": 8.2,
  "Track": 9.0,
  "General": 8.0
};

export function calculateVelocityLossMetrics(trials: Trial[], vFree: number): VelocityLossMetrics {
  const velocities = trials.map(t => t.v_max_ms);
  const vMin = Math.min(...velocities);
  const loadedTrials = trials.filter(t => t.load_kgf > 0);
  const vMaxLoaded = loadedTrials.length > 0 ? Math.max(...loadedTrials.map(t => t.v_max_ms)) : vMin;
  const maxLoad = Math.max(...trials.map(t => t.load_kgf));

  return {
    totalVelocityLoss: ((vFree - vMin) / vFree) * 100,
    velocityLossPerKg: maxLoad > 0 ? (vFree - vMaxLoaded) / maxLoad : 0,
    velocityDeficit: vFree - vMaxLoaded
  };
}

export function calculateLoadVelocityMetrics(fvProfile: FVProfile, vFree: number): LoadVelocityMetrics {
  const { F0_N, slope } = fvProfile;
  
  return {
    minimumVelocityLoad: F0_N / 9.80665,
    loadAt50Velocity: (F0_N - slope * (vFree * 0.5)) / 9.80665
  };
}

export function calculatePowerDistribution(trials: Trial[]): PowerDistribution {
  const powers = trials.map(trial => {
    const force = trial.load_kgf * 9.80665;
    return force * trial.v_max_ms;
  });
  
  const maxPower = Math.max(...powers);
  const maxPowerIndex = powers.indexOf(maxPower);
  const optimalLoad = trials[maxPowerIndex].load_kgf;
  
  const power90Threshold = maxPower * 0.9;
  const validIndices = powers.map((p, i) => p >= power90Threshold ? i : -1).filter(i => i >= 0);
  const power90Range: [number, number] = [
    Math.min(...validIndices.map(i => trials[i].load_kgf)),
    Math.max(...validIndices.map(i => trials[i].load_kgf))
  ];

  return {
    optimalLoad,
    powerAt90Range: power90Range,
    powerAtEachLoad: powers
  };
}

export function calculateMechanicalEfficiency(trials: Trial[], fvProfile: FVProfile, vFree: number, massKg: number): MechanicalEfficiency {
  const { F0_N, slope } = fvProfile;
  const loadedTrials = trials.filter(t => t.load_kgf > 0);
  const vMaxLoaded = loadedTrials.length > 0 ? Math.max(...loadedTrials.map(t => t.v_max_ms)) : vFree;
  const maxLoad = Math.max(...trials.map(t => t.load_kgf));

  const forceDeficits = trials.map(trial => {
    const appliedForce = trial.load_kgf * 9.80665;
    const theoreticalForce = F0_N + slope * trial.v_max_ms;
    return theoreticalForce - appliedForce;
  });

  return {
    velocityPreservation: (vMaxLoaded / vFree) * 100,
    loadTolerance: maxLoad > 0 ? (maxLoad / massKg) * 100 : 0,
    forceDeficits
  };
}

export function calculateTrainingReadiness(fvProfile: FVProfile, vFree: number, sport: string): TrainingReadiness {
  const racePace = RACE_PACE_ESTIMATES[sport] || RACE_PACE_ESTIMATES["General"];
  
  return {
    speedReserve: vFree - racePace,
    accelerationIndex: Math.abs(fvProfile.slope),
    neuromuscularReadiness: fvProfile.R2
  };
}

export function calculatePerformanceRatios(fvProfile: FVProfile, vFree: number, massKg: number): PerformanceRatios {
  return {
    powerToWeightRatio: fvProfile.Pmax_rel,
    forceToWeightRatio: fvProfile.F0_N / (massKg * 9.80665),
    velocityEfficiency: fvProfile.V0 / vFree,
    normalizedSlope: fvProfile.slope / vFree
  };
}

export function calculateDistanceMetrics(vFree: number, fvProfile: FVProfile, sprintDistance: number): DistanceMetrics {
  return {
    estimatedSprintTime: sprintDistance / vFree,
    averageAcceleration: (vFree * vFree) / (2 * sprintDistance),
    powerPerMeter: fvProfile.Pmax / sprintDistance,
    velocityPerMeter: vFree / sprintDistance
  };
}

export function calculateValidationMetrics(trials: Trial[], sprintDistance: number): ValidationMetrics | undefined {
  const trialsWithPower = trials.filter(t => t.power_W !== undefined);
  const trialsWithTime = trials.filter(t => t.sprint_time_s !== undefined);
  
  if (trialsWithPower.length === 0 && trialsWithTime.length === 0) return undefined;
  
  const powerAccuracy = trialsWithPower.map(trial => {
    const calculatedPower = (trial.load_kgf * 9.80665) * trial.v_max_ms;
    return Math.abs(trial.power_W! - calculatedPower) / trial.power_W! * 100;
  });
  
  const timeAccuracy = trialsWithTime.map(trial => {
    const calculatedTime = sprintDistance / trial.v_max_ms;
    return Math.abs(trial.sprint_time_s! - calculatedTime) / trial.sprint_time_s! * 100;
  });
  
  const measurementErrors = [...powerAccuracy, ...timeAccuracy].filter(error => error > 10).length;
  
  return {
    powerAccuracy,
    timeAccuracy,
    measurementErrors
  };
}

export function calculateMechanicalAnalysis(trials: Trial[], fvProfile: FVProfile): MechanicalAnalysis | undefined {
  const trialsWithPower = trials.filter(t => t.power_W !== undefined);
  if (trialsWithPower.length < 2) return undefined;
  
  const actualVsTheoreticalPower = trialsWithPower.map(trial => {
    const theoreticalPower = (fvProfile.F0_N + fvProfile.slope * trial.v_max_ms) * trial.v_max_ms;
    return (trial.power_W! / theoreticalPower) * 100;
  });
  
  const powers = trialsWithPower.map(t => t.power_W!);
  const meanPower = powers.reduce((a, b) => a + b, 0) / powers.length;
  const powerVariance = powers.reduce((acc, p) => acc + Math.pow(p - meanPower, 2), 0) / powers.length;
  const powerConsistency = (Math.sqrt(powerVariance) / meanPower) * 100;
  
  const mechanicalEffectiveness = trialsWithPower.map(trial => {
    const force = trial.load_kgf * 9.80665;
    return (trial.power_W! / force) / trial.v_max_ms * 100;
  });
  
  const neuromuscularEfficiency = actualVsTheoreticalPower.reduce((a, b) => a + b, 0) / actualVsTheoreticalPower.length;
  
  return {
    actualVsTheoreticalPower,
    powerConsistency,
    mechanicalEffectiveness,
    neuromuscularEfficiency
  };
}

export function calculateFatigueMetrics(trials: Trial[]): FatigueMetrics | undefined {
  const trialsWithPower = trials.filter(t => t.power_W !== undefined);
  if (trialsWithPower.length < 3) return undefined;
  
  const powers = trialsWithPower.map(t => t.power_W!);
  const maxPower = Math.max(...powers);
  const minPower = Math.min(...powers);
  
  const fatigueIndex = ((maxPower - minPower) / maxPower) * 100;
  const powerDropOff = ((powers[0] - powers[powers.length - 1]) / powers[0]) * 100;
  
  // Simple recovery estimation based on power maintenance
  const recoveryIndex = (minPower / maxPower) * 100;
  const sustainabilityScore = 100 - fatigueIndex;
  
  return {
    fatigueIndex,
    powerDropOff,
    recoveryIndex,
    sustainabilityScore
  };
}

export function calculateAdvancedProfiling(trials: Trial[], sprintDistance: number): AdvancedProfiling | undefined {
  const trialsWithBoth = trials.filter(t => t.power_W !== undefined && t.sprint_time_s !== undefined);
  if (trialsWithBoth.length < 2) return undefined;
  
  const powers = trialsWithBoth.map(t => t.power_W!);
  const times = trialsWithBoth.map(t => t.sprint_time_s!);
  
  const maxPowerIndex = powers.indexOf(Math.max(...powers));
  const peakPowerTiming = times[maxPowerIndex];
  
  // Estimate acceleration phases (0-33%, 33-66%, 66-100% of distance)
  const accelerationPhases = trialsWithBoth.map(trial => {
    const avgAccel = (2 * sprintDistance) / (trial.sprint_time_s! * trial.sprint_time_s!);
    return avgAccel;
  });
  
  // Metabolic contribution estimation based on power sustainability
  const metabolicContribution = (Math.min(...powers) / Math.max(...powers)) * 100;
  
  // Power-time relationship slope
  const n = times.length;
  const sumT = times.reduce((a, b) => a + b, 0);
  const sumP = powers.reduce((a, b) => a + b, 0);
  const sumTP = times.reduce((acc, t, i) => acc + t * powers[i], 0);
  const sumT2 = times.reduce((acc, t) => acc + t * t, 0);
  
  const powerTimeSlope = (n * sumTP - sumT * sumP) / (n * sumT2 - sumT * sumT);
  
  return {
    peakPowerTiming,
    accelerationPhases,
    metabolicContribution,
    powerTimeSlope
  };
}

export function calculateInjuryPreventionIndicators(trials: Trial[], fvProfile: FVProfile, velocityLoss: VelocityLossMetrics): InjuryPreventionIndicators | undefined {
  const trialsWithPower = trials.filter(t => t.power_W !== undefined);
  if (trialsWithPower.length < 2) {
    // Calculate basic indicators without power data
    const velocityDecline = velocityLoss.totalVelocityLoss;
    const loadRange = Math.max(...trials.map(t => t.load_kgf)) - Math.min(...trials.map(t => t.load_kgf));
    
    return {
      powerAsymmetry: 0, // Cannot calculate without bilateral power data
      fatigueThreshold: velocityDecline > 50 ? 85 : 100 - velocityDecline,
      overreachingRisk: velocityDecline > 60 ? 75 : velocityDecline > 40 ? 50 : 25,
      techniqueBreakdown: fvProfile.R2 < 0.95 ? 75 : 25,
      loadTolerance: Math.min(100, (loadRange / 50) * 100)
    };
  }
  
  const powers = trialsWithPower.map(t => t.power_W!);
  const velocities = trialsWithPower.map(t => t.v_max_ms);
  
  // Power asymmetry (simplified - would need bilateral measurement for true asymmetry)
  const powerCV = (Math.sqrt(powers.reduce((acc, p) => acc + Math.pow(p - powers.reduce((a, b) => a + b) / powers.length, 2), 0) / powers.length) / (powers.reduce((a, b) => a + b) / powers.length)) * 100;
  const powerAsymmetry = Math.min(100, powerCV * 2); // Estimate asymmetry from power variability
  
  // Fatigue threshold - power level where significant decline occurs
  const maxPower = Math.max(...powers);
  const powerDecline = ((powers[0] - powers[powers.length - 1]) / powers[0]) * 100;
  const fatigueThreshold = 100 - Math.min(powerDecline * 2, 50);
  
  // Overreaching risk based on power-velocity relationship deviation
  const expectedPowers = trialsWithPower.map(trial => {
    const force = trial.load_kgf * 9.80665;
    return force * trial.v_max_ms;
  });
  const powerDeviations = powers.map((p, i) => Math.abs(p - expectedPowers[i]) / expectedPowers[i] * 100);
  const avgDeviation = powerDeviations.reduce((a, b) => a + b) / powerDeviations.length;
  const overreachingRisk = Math.min(100, avgDeviation * 3);
  
  // Technique breakdown indicator
  const velocityConsistency = 100 - ((Math.max(...velocities) - Math.min(...velocities)) / Math.max(...velocities) * 100);
  const techniqueBreakdown = 100 - Math.min(velocityConsistency, fvProfile.R2 * 100);
  
  // Load tolerance
  const maxLoad = Math.max(...trialsWithPower.map(t => t.load_kgf));
  const loadTolerance = Math.min(100, (maxLoad / 40) * 100); // Normalized to 40kg as high load
  
  return {
    powerAsymmetry,
    fatigueThreshold,
    overreachingRisk,
    techniqueBreakdown,
    loadTolerance
  };
}